import { faCalendarAlt } from "@fortawesome/pro-regular-svg-icons";
import { faAngleLeft, faAngleRight, faAnglesLeft, faAnglesRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CalendarDate, parseAbsolute, toZoned } from "@internationalized/date";
import classNames from "classnames";
import { addDays, addHours, addMonths, addWeeks, addYears, format, isValid, parse } from "date-fns";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Button as AriaButton,
  DatePicker as AriaDatePicker,
  Label as AriaLabel,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarState,
  DateValue,
  Dialog,
  Group,
  Heading,
  OverlayArrow,
  Popover,
} from "react-aria-components";
import { Control, Controller, DeepMap, FieldError, RegisterOptions } from "react-hook-form";
import { Button, Label, Segment } from "semantic-ui-react";
import { useTimezone } from "../../../../hooks/useTimezone";
import { fieldError } from "../../../../utils/functions";
import { ZonedDateTime, ZonedDateTimeProps } from "../../ZonedDateTime";
import { TZDate } from "@date-fns/tz";
import { createPortal } from "react-dom";
import {
  AccessibleDropdown,
  AccessibleDropdownOption,
  CustomControl,
  CustomValueContainer,
} from "./AccessibleDropdown";
import { components } from "react-select";

type Transform<T> = {
  input: (value: T | null | undefined) => Date | string | null | undefined;
  output: (value: Date | string | null | undefined) => T | null | undefined;
};

type SharedDatePickerProps = {
  id?: string;
  timeWithRespectToAgencyTimezone?: boolean;
  readonly?: boolean;
  isDateUnavailable?: (date: DateValue) => boolean;
  UNSTABLE_portalContainer?: Element | undefined;
  label?: ReactNode;
  required?: boolean;
  name?: string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  bgClassName?: string;
  noBorder?: boolean;
  paddingClassName?: string;
  calendarButtonSizeClassName?: string;
  calendarIconClassName?: string;
  placeholder?: string;
  clearable?: boolean;
  zonedDateTimeProps?: Partial<ZonedDateTimeProps>;
  onBlur?: () => void;
  readonlyFormat?: string;
  readonlyInput?: boolean;
  headerMode?: "single" | "default";
  forbidNullDate?: boolean;
  showErrors?: boolean;
  hourOffset?: number; // Number of hours to offset the date by for display purposes
  portalErrorLabel?: boolean;
  yearless?: boolean;
  transform?: Transform<any>;
  useClientTimezone?: boolean;
  useAgencyTimezone?: boolean;
  calendarPosition?: "left" | "right" | "none";
  // Normally this component disallows invalid values from making it into the form state
  // Turn this on to allow it, useful to use yup based validation rather than component rules
  allowInvalidFormValues?: boolean;
};

type ControlledDatePickerProps<FormValue, Key extends string> = SharedDatePickerProps & {
  control: Control;
  name: string;
  defaultValue?: Date | string;
  errors?: DeepMap<Record<Key, FormValue>, FieldError>;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export function ControlledDatePicker<FormValue, Key extends string>(props: ControlledDatePickerProps<FormValue, Key>) {
  const {
    id,
    control,
    name,
    label,
    timeWithRespectToAgencyTimezone = true,
    readonly = false,
    errors,
    required = false,
    rules,
    placeholder,
    className,
    labelClassName,
    bgClassName,
    noBorder,
    paddingClassName,
    calendarButtonSizeClassName,
    calendarIconClassName,
    clearable = false,
    defaultValue,
    onBlur,
    zonedDateTimeProps,
    readonlyFormat,
    readonlyInput,
    headerMode,
    forbidNullDate,
    showErrors = false,
    hourOffset,
    portalErrorLabel = false,
    yearless = false,
    allowInvalidFormValues = false,
    transform,
    useClientTimezone,
    useAgencyTimezone,
    inputClassName,
    calendarPosition,
  } = props;

  const [validationError, setValidationError] = useState<string | null>(null);

  const error = fieldError(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={validationError ? { validate: () => validationError } : rules}
      render={({ onChange, value }) => {
        return (
          <DatePicker
            id={id}
            timeWithRespectToAgencyTimezone={timeWithRespectToAgencyTimezone}
            readonly={readonly}
            value={value}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            label={label}
            required={required}
            placeholder={placeholder}
            error={error?.content.message}
            UNSTABLE_portalContainer={props.UNSTABLE_portalContainer}
            isDateUnavailable={props.isDateUnavailable}
            className={className}
            bgClassName={bgClassName}
            noBorder={noBorder}
            paddingClassName={paddingClassName}
            calendarButtonSizeClassName={calendarButtonSizeClassName}
            calendarIconClassName={calendarIconClassName}
            clearable={clearable}
            zonedDateTimeProps={zonedDateTimeProps}
            readonlyFormat={readonlyFormat}
            readonlyInput={readonlyInput}
            headerMode={headerMode}
            forbidNullDate={forbidNullDate}
            onValidityChange={(msg) => {
              setValidationError(msg);
              // Wait for refresh cycle so rules are updated with the message before triggering validation
              setTimeout(() => {
                control.trigger(name);
              }, 0);
            }}
            showErrors={showErrors}
            hourOffset={hourOffset}
            portalErrorLabel={portalErrorLabel}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            yearless={yearless}
            allowInvalidFormValues={allowInvalidFormValues}
            transform={transform}
            useClientTimezone={useClientTimezone}
            useAgencyTimezone={useAgencyTimezone}
            calendarPosition={calendarPosition}
          />
        );
      }}
    />
  );
}

type DatePickerProps = SharedDatePickerProps & {
  readonly?: boolean;
  error?: string;
  value?: Date;
  onChange?: ((value?: Date | null) => void) | undefined;
  onValidityChange?: ((message: string | null) => void) | undefined;
};

// When opening the calendar, the focus moves between various elements and may make onBlur handlers fire preemptively
// Use this to check if it actually has lost focus - note that you should set a timeout for this check to give
// time for the focus to move to the calendar
export function checkCalendarFocus() {
  const activeElement = document.activeElement;
  const isCalendarRelated =
    activeElement?.getAttribute("aria-label") === "Calendar" || // Date picker button
    activeElement?.hasAttribute("data-react-aria-pressable") || // Calendar cells
    activeElement?.closest('[role="dialog"]') || // Inside a dialog
    activeElement?.hasAttribute("data-rac") || // Various React Aria Calendar elements
    activeElement?.closest(".date-picker-input"); // Date picker input itself
  return isCalendarRelated;
}

function parseRelativeDate(input: string): Date | null | undefined {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return undefined;
  const today = new TZDate(new Date(), "UTC");

  const relativePattern = /^(\d+)\s*(day|days|week|weeks|month|months|year|years)$/;
  const match = trimmed.match(relativePattern);
  if (match) {
    const amount = parseInt(match[1], 10);
    const unit = match[2];

    switch (unit) {
      case "day":
      case "days":
        return addDays(today, amount);
      case "week":
      case "weeks":
        return addWeeks(today, amount);
      case "month":
      case "months":
        return addMonths(today, amount);
      case "year":
      case "years":
        return addYears(today, amount);
    }
  }
  return null;
}

// Undefined = no input, Null = invalid input
function parseFlexibleDate(input: string): Date | null | undefined {
  if (!input) return undefined;
  const trimmed = input.trim();

  const relativeDate = parseRelativeDate(trimmed);
  if (relativeDate) return relativeDate;

  const digitsOnly = trimmed.replace(/\D/g, "");

  // Compact format (e.g., "09052005" -> "09/05/2005")
  if (digitsOnly.length === 8) {
    const month = digitsOnly.substring(0, 2);
    const day = digitsOnly.substring(2, 4);
    const year = digitsOnly.substring(4, 8);
    const compactDate = parse(`${month}/${day}/${year}`, "MM/dd/yyyy", new TZDate(new Date(), "UTC"));
    if (isValid(compactDate)) return compactDate;
  }

  // Normalize separators to forward slash
  const normalized = trimmed.replace(/[\s.-]/g, "/");
  const parts = normalized.split("/").filter((p) => p.length > 0);
  if (!parts.length) return undefined;

  const currentYear = new TZDate(new Date(), "UTC").getFullYear();
  let month = "",
    day = "",
    year = "";

  if (parts.length === 1) {
    // Could be compact format or just month
    if (parts[0].length >= 3) {
      // Try to parse as MMDD
      month = parts[0].substring(0, 2);
      day = parts[0].substring(2, 4) || "";
      year = parts[0].substring(4) || "";
    } else {
      month = parts[0];
    }
  } else if (parts.length === 2) {
    month = parts[0];
    day = parts[1];
    year = String(currentYear);
  } else if (parts.length === 3) {
    month = parts[0];
    day = parts[1];
    year = parts[2];
  }

  month = month.padStart(2, "0");
  day = day.padStart(2, "0");

  // 2-digit year with pivot (50 = 1950-2049)
  if (year.length === 2) {
    const yearNum = parseInt(year, 10);
    year = String(yearNum < 50 ? 2000 + yearNum : 1900 + yearNum);
  } else if (year.length === 0) {
    year = String(currentYear);
  }

  if (!month || !day || !year) return null;

  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  if (monthNum < 1 || monthNum > 12) return null;
  if (dayNum < 1 || dayNum > 31) return null;

  const parsed = parse(`${month}/${day}/${year}`, "MM/dd/yyyy", new TZDate(new Date(), "UTC"));
  if (!isValid(parsed)) return null;
  return parsed;
}

export const DatePicker = ({
  id,
  timeWithRespectToAgencyTimezone = true,
  readonly = false,
  isDateUnavailable,
  UNSTABLE_portalContainer,
  error,
  value,
  name,
  onChange,
  onBlur,
  label,
  required = false,
  className,
  labelClassName,
  inputClassName,
  bgClassName = "tw-bg-white",
  noBorder = false,
  paddingClassName = "tw-px-4 tw-py-[0.53rem] tw-h-[38px]",
  calendarButtonSizeClassName = "tw-w-[14px] tw-h-gg-4",
  // Normally requires a slight offset to align visually, override with different sizes
  calendarIconClassName = "tw-mb-[2px]",
  placeholder,
  clearable = false,
  zonedDateTimeProps,
  readonlyFormat,
  readonlyInput = false,
  headerMode = "default",
  forbidNullDate = false,
  onValidityChange,
  showErrors = false,
  hourOffset,
  portalErrorLabel = false,
  yearless = false,
  allowInvalidFormValues,
  transform = { input: (v) => v, output: (v) => v },
  useClientTimezone,
  useAgencyTimezone,
  calendarPosition = "right",
}: DatePickerProps) => {
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timezoneInfo = useTimezone({ defaultToAgencyTimezone: timeWithRespectToAgencyTimezone });
  const timezone = "UTC";
  if (useClientTimezone) hourOffset ||= -timezoneInfo.clientTimezoneInfo.utcOffset;
  if (useAgencyTimezone) hourOffset ||= -timezoneInfo.defaultTimezoneInfo.utcOffset;

  const transformedInput = transform.input(value);

  const [errorLabelPosition, setErrorLabelPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const groupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateErrorPosition = () => {
      if ((error || validationError) && groupRef.current) {
        const rect = groupRef.current.getBoundingClientRect();
        setErrorLabelPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      } else {
        setErrorLabelPosition(null);
      }
    };
    updateErrorPosition();

    window.addEventListener("scroll", updateErrorPosition, true);
    window.addEventListener("resize", updateErrorPosition);

    return () => {
      window.removeEventListener("scroll", updateErrorPosition, true);
      window.removeEventListener("resize", updateErrorPosition);
    };
  }, [error, validationError]);

  function tryFormatDate(value: Date): string {
    try {
      return format(new TZDate(value, "UTC"), yearless ? "MM/dd" : "MM/dd/yyyy");
    } catch (e: unknown) {
      return "";
    }
  }

  // Initialize input value from prop value
  useEffect(() => {
    if (transformedInput && !focused && !validationError) {
      const inputValue = tryFormatDate(
        typeof transformedInput === "string" ? new Date(transformedInput) : transformedInput,
      );
      setInputValue(inputValue);
      setValidationError(null);
      onValidityChange?.(null);
    } else if (!transformedInput && !focused && !validationError) {
      setInputValue("");
      setValidationError(null);
      onValidityChange?.(null);
    }
  }, [transformedInput, focused]);

  function tryParseAbsolute(value: Date | string, timezone: string) {
    try {
      return parseAbsolute(new TZDate(new Date(value), "UTC").toISOString(), timezone);
    } catch {
      return null;
    }
  }

  const shouldHideTimezone = !timeWithRespectToAgencyTimezone || timezoneInfo.clientIsInAgencyTimezone;
  const zonedDate = transformedInput ? tryParseAbsolute(transformedInput, "UTC") : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    const allowedChars = /^[0-9\/\-.\s a-zA-Z]*$/; // Allow only digits, /, -, ., spaces, and letters (for relative dates)
    if (!allowedChars.test(input)) return;
    setInputValue(input);
    setValidationError(null);
    onValidityChange?.(null);
  };

  const handleInputBlur = () => {
    setFocused(false);

    if (!inputValue.trim()) {
      !forbidNullDate && onChange?.(transform.output(undefined));
      setValidationError(null);
      onValidityChange?.(null);
      return;
    }

    const parsed = parseFlexibleDate(inputValue);

    if (parsed) {
      // Format and display the parsed date
      if (
        isDateUnavailable?.(
          toZoned(new CalendarDate(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate()), timezone),
        )
      ) {
        setValidationError("Selected date is unavailable");
        onValidityChange?.("Selected date is unavailable");
        if (allowInvalidFormValues) {
          const tzDate = new TZDate(parsed, "UTC");
          tzDate.setHours(0, 0, 0, 0);
          onChange?.(transform.output(addHours(tzDate, hourOffset || 0)));
        }
        return;
      }
      const formatted = format(parsed, yearless ? "MM/dd" : "MM/dd/yyyy");
      setInputValue(formatted);
      setValidationError(null);
      onValidityChange?.(null);
      const tzDate = new TZDate(parsed, "UTC");
      tzDate.setHours(0, 0, 0, 0);
      onChange?.(transform.output(addHours(tzDate, hourOffset || 0)));
    } else {
      setValidationError("Invalid date format");
      onValidityChange?.("Invalid date format");
      if (allowInvalidFormValues) onChange?.(transform.output(null));
    }

    if (onBlur) onBlur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === " ") e.stopPropagation();
    if (e.key === "Enter") handleInputBlur();
  };

  const handleCalendarChange = (value: DateValue | null) => {
    if (!value) return;
    const date = toZoned(value, timezone).toDate();
    const tzDate = new TZDate(date, "UTC");
    tzDate.setHours(0, 0, 0, 0);
    onChange?.(transform.output(addHours(tzDate, hourOffset || 0)));
    setInputValue(format(date, yearless ? "MM/dd" : "MM/dd/yyyy"));
    setValidationError(null);
    onValidityChange?.(null);
  };

  const handleClear = () => {
    setInputValue("");
    !forbidNullDate && onChange?.(transform.output(undefined));
    setValidationError(null);
    onValidityChange?.(null);
  };

  if (readonly) {
    return (
      <>
        {label && <label>{label}</label>}
        <ZonedDateTime
          value={zonedDate?.toDate()}
          {...zonedDateTimeProps}
          formatString={readonlyFormat}
          timezone={"UTC"}
        />
      </>
    );
  }

  return (
    <AriaDatePicker
      value={zonedDate}
      onChange={handleCalendarChange}
      hideTimeZone={shouldHideTimezone}
      granularity='day'
      className={classNames(className, "tw-flex tw-flex-col")}
    >
      {label && (
        <AriaLabel className='label-text tw-mb-1'>
          {label}
          {required && <span className='tw-ml-1 tw-text-red'>*</span>}
        </AriaLabel>
      )}
      <Group
        ref={groupRef}
        className={classNames({
          "accessible-input tw-flex tw-items-center tw-justify-between tw-rounded-[.285rem] tw-border-[1px] tw-border-solid tw-transition-shadow tw-duration-75": true,
          "tw-border-solid": !noBorder,
          "tw-border-none": noBorder,
          [paddingClassName]: true,
          "tw-border-[#22242626]": !focused,
          "tw-border-[#7faecfbf]": focused,
          "tw-border-[#E0B4B4] tw-bg-[#FFF6F6]": !!error || !!validationError,
          [bgClassName]: !error && !validationError,
          "tw-flex-row-reverse": calendarPosition === "left",
        })}
      >
        <div className='tw-flex tw-items-center'>
          <input
            ref={inputRef}
            id={id}
            name={name}
            type='text'
            value={inputValue}
            readOnly={readonlyInput}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKeyDown}
            /* These events prevent React Aria from stealing focus and forcing it onto the calendar button */
            /* It bugs out with the focus management with vanilla html inputs */
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              // if its readonly, open the calendar on click
              if (readonlyInput) {
                e.stopPropagation();
                const button = groupRef.current?.querySelector('button[aria-label="Calendar"]') as HTMLButtonElement;
                button?.click();
              }
            }}
            placeholder={placeholder || (yearless ? "MM/DD" : "MM/DD/YYYY")}
            className={classNames(
              inputClassName,
              "date-picker-input tw-tg-body-medium tw-flex-1 tw-border-none tw-bg-transparent tw-p-0 tw-font-normal tw-outline-none focus:tw-outline-none",
            )}
            aria-invalid={Boolean(error || validationError)}
          />
          {clearable && inputValue && (
            <button
              type='button'
              onClick={handleClear}
              className='clear-button tw-mr-1 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-font-bold tw-leading-[1] tw-text-[#000]'
              aria-label='Clear'
            >
              ✕
            </button>
          )}
        </div>
        {calendarPosition !== "none" && (
          <AriaButton
            className={classNames(
              "accessible-input tw-mr-0 tw-flex tw-items-center tw-justify-center tw-rounded-sm tw-border-none tw-bg-transparent tw-p-0 tw-text-primary-hint hover:tw-bg-white-background focus:tw-ring-2 focus:tw-ring-[#7faecfbf] focus:tw-ring-offset-2 focus-visible:tw-outline-none",
              {
                [calendarButtonSizeClassName]: true,
              },
            )}
          >
            <FontAwesomeIcon
              className={`tw-h-full tw-w-full tw-font-bold ${calendarIconClassName ?? ""}`}
              icon={faCalendarAlt}
            />
          </AriaButton>
        )}
      </Group>
      {(error || validationError) &&
        showErrors &&
        errorLabelPosition &&
        (portalErrorLabel ? (
          createPortal(
            <Label
              color='red'
              basic
              prompt
              pointing='above'
              role='alert'
              aria-live='polite'
              style={{
                position: "absolute",
                top: `${errorLabelPosition.top}px`,
                left: `${errorLabelPosition.left}px`,
                width: `${errorLabelPosition.width}px`,
                marginTop: "12px",
                zIndex: 10,
              }}
              className={labelClassName}
            >
              {error || validationError}
            </Label>,
            document.body,
          )
        ) : (
          <Label color='red' basic prompt pointing='above' role='alert' aria-live='polite' className={labelClassName}>
            {error || validationError}
          </Label>
        ))}
      <Popover className='tw-absolute' UNSTABLE_portalContainer={UNSTABLE_portalContainer} placement='bottom end'>
        <OverlayArrow />
        <Segment className='tw-m-0 tw-shadow-md'>
          <Dialog>
            <Calendar isDateUnavailable={isDateUnavailable} className={"tw-flex tw-flex-col tw-items-center"}>
              {({ state }) => (
                <>
                  <CalendarHeader state={state} headerMode={headerMode} yearless={yearless} />
                  <CalendarGrid weekdayStyle='short'>
                    <CalendarGridHeader>
                      {(day) => (
                        <CalendarHeaderCell className='tw-tg-label-small tw-p-[10px] tw-text-center tw-font-bold'>
                          {day}
                        </CalendarHeaderCell>
                      )}
                    </CalendarGridHeader>
                    <CalendarGridBody>
                      {(date) => {
                        return (
                          <CalendarCell
                            className={({ isUnavailable, isDisabled, isSelected, isOutsideMonth, isToday }) => {
                              const base =
                                "tw-tg-label-medium tw-flex tw-size-gg-7 tw-cursor-default tw-items-center tw-justify-center tw-text-sm tw-forced-color-adjust-none";
                              if (isDisabled || isUnavailable) return `${base} tw-rounded-sm tw-text-grey-70`;
                              if (isSelected) return `${base} tw-rounded-sm tw-bg-blue tw-text-white`;
                              if (isToday)
                                return `${base} tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-border-primary`;
                              if (!isOutsideMonth)
                                return `${base} tw-rounded-sm hover:tw-cursor-pointer hover:tw-bg-white-background`;
                              return `${base} tw-rounded-sm tw-text-text-secondary`;
                            }}
                            date={date}
                          />
                        );
                      }}
                    </CalendarGridBody>
                  </CalendarGrid>
                </>
              )}
            </Calendar>
          </Dialog>
        </Segment>
      </Popover>
    </AriaDatePicker>
  );
};

const CalendarHeader = ({
  state,
  headerMode,
  yearless,
}: {
  state: CalendarState;
  headerMode?: "single" | "default";
  yearless?: boolean;
}) => {
  const monthDropdownRef = useRef<HTMLDivElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);
  const months = useMemo(
    () =>
      yearless
        ? [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ]
        : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    [],
  );
  const monthOptions = useMemo(
    () => months.map((month, index) => ({ key: month, value: index, label: month })),
    [months],
  );
  const currentMonth = useMemo(() => state.visibleRange.start.month - 1, [state.visibleRange.start.month]);
  const currentYear = useMemo(() => state.visibleRange.start.year, [state.visibleRange.start.year]);
  const years = useMemo(
    () => Array.from({ length: 100 }, (_, i) => currentYear - 50 + i).filter((year) => year > 0),
    [currentYear],
  );
  const yearOptions = useMemo(
    () => years.map((year) => ({ key: year.toString(), value: year, label: year.toString() })),
    [years],
  );

  if (headerMode === "single") {
    return (
      <div className={"tw-mb-1 tw-flex tw-w-full tw-items-center tw-justify-between tw-px-1"}>
        <div className='tw-flex tw-gap-1'>
          <AriaButton
            onPress={() => state.setFocusedDate(state.focusedDate.subtract({ years: 1 }))}
            className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'
            aria-label='Previous year'
            slot='previous'
          >
            <Button
              as='div'
              className='tw-mr-0 tw-flex tw-size-gg-5 tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white-background'
              icon
              tabIndex={-1}
            >
              <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-primary-hint' icon={faAnglesLeft} />
            </Button>
          </AriaButton>
          <AriaButton slot='previous' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
            <Button
              as='div'
              className='tw-mr-0 tw-flex tw-size-gg-5 tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white-background'
              icon
              tabIndex={-1}
            >
              <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-primary-hint' icon={faAngleLeft} />
            </Button>
          </AriaButton>
        </div>
        <Heading className='tw-m-0 tw-text-xl' />
        <div className='tw-flex tw-gap-1'>
          <AriaButton slot='next' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
            <Button
              as='div'
              className='tw-mr-0 tw-flex tw-size-gg-5 tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white-background'
              icon
              tabIndex={-1}
            >
              <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-primary-hint' icon={faAngleRight} />
            </Button>
          </AriaButton>
          <AriaButton
            onPress={() => state.setFocusedDate(state.focusedDate.add({ years: 1 }))}
            className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'
            aria-label='Next year'
            slot='next'
          >
            <Button
              as='div'
              className='tw-mr-0 tw-flex tw-size-gg-5 tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white-background'
              icon
              tabIndex={-1}
            >
              <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-primary-hint' icon={faAnglesRight} />
            </Button>
          </AriaButton>
        </div>
      </div>
    );
  }

  return (
    <div className={"tw-mb-1 tw-flex tw-w-full tw-items-center tw-justify-between tw-px-1"}>
      <AriaButton slot='previous' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
        <Button
          as='div'
          className='tw-mr-0 tw-flex tw-size-gg-5 tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white-background'
          icon
          tabIndex={-1}
        >
          <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-primary-hint' icon={faAngleLeft} />
        </Button>
      </AriaButton>

      <div className='tw-flex tw-gap-2'>
        <div className='tw-relative' ref={monthDropdownRef}>
          <AccessibleDropdown
            value={monthOptions.find((option) => option.value === currentMonth)}
            dropdownOnly
            options={monthOptions}
            onMenuOpen={() => {
              // React select's built in menuShouldScrollIntoView doesn't scroll all the way for some reason, we need to do it ourselves
              setTimeout(() => {
                monthDropdownRef.current?.querySelectorAll('div[class$="-option"]').forEach((optionDiv) => {
                  if (optionDiv.textContent !== months[currentMonth]) return;
                  optionDiv.parentElement?.parentElement?.scrollIntoView({ block: "center" });
                });
              }, 15);
            }}
            onChange={(value) => {
              if (value == null) return;
              const month = (value as AccessibleDropdownOption<any>).value;
              if (state.focusedDate.month - 1 === month) return;
              state.setFocusedDate(state.focusedDate.add({ months: month - currentMonth }));
            }}
            components={{
              valueContainer: DatePickerValueContainer,
              control: DatePickerControl,
            }}
            className={classNames(
              "tw-tg-label-medium-prominent tw-h-gg-6 tw-rounded-sm tw-border tw-border-solid tw-border-border-dark tw-bg-white focus:tw-border-[#7faecfbf] focus:tw-outline-none",
              {
                "tw-w-[98px]": !yearless,
                "tw-w-[120px]": yearless,
              },
            )}
            aria-label='Month'
          />
        </div>

        {!yearless && (
          <div className='tw-relative' ref={yearDropdownRef}>
            <AccessibleDropdown
              value={yearOptions.find((option) => option.value === currentYear)}
              dropdownOnly
              options={yearOptions}
              onMenuOpen={() => {
                // React select's built in menuShouldScrollIntoView doesn't scroll all the way for some reason, we need to do it ourselves
                setTimeout(() => {
                  yearDropdownRef.current?.querySelectorAll('div[class$="-option"]').forEach((optionDiv) => {
                    if (optionDiv.textContent !== `${currentYear}`) return;
                    optionDiv.parentElement?.parentElement?.scrollIntoView({ block: "center" });
                  });
                }, 50);
              }}
              onChange={(value) => {
                if (value == null) return;
                const year = (value as AccessibleDropdownOption<any>).value;
                if (state.focusedDate.year === year) return;
                state.setFocusedDate(state.focusedDate.add({ years: year - currentYear }));
              }}
              components={{
                valueContainer: DatePickerValueContainer,
                control: DatePickerControl,
              }}
              className='tw-tg-label-medium-prominent tw-h-gg-6 tw-w-[98px] tw-rounded-sm tw-border tw-border-solid tw-border-border-dark tw-bg-white focus:tw-border-[#7faecfbf] focus:tw-outline-none'
              aria-label='Year'
            />
          </div>
        )}
      </div>

      <AriaButton slot='next' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
        <Button
          as='div'
          className='tw-mr-0 tw-flex tw-size-gg-5 tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white-background'
          icon
          tabIndex={-1}
        >
          <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-primary-hint' icon={faAngleRight} />
        </Button>
      </AriaButton>
    </div>
  );
};

const DatePickerValueContainer: CustomValueContainer = ({ children, ...props }) => (
  <components.ValueContainer {...props} className='tw-cursor-pointer tw-p-0'>
    {children}
  </components.ValueContainer>
);

const DatePickerControl: CustomControl = ({ ...props }) => (
  <components.Control
    {...props}
    className='tw-h-gg-6 tw-max-h-gg-6 tw-min-h-gg-6 tw-cursor-pointer tw-border-none tw-bg-transparent tw-px-gg-3 tw-shadow-none'
  />
);
